/**
 * Demo-utgave av datalaget. Kjører helt i nettleseren med localStorage som
 * lagring, og speiler reglene i databasen (godkjenning, grenser per dag/uke,
 * transaksjoner). Brukes når Supabase ikke er satt opp - da kan appen prøves
 * med én gang - og som en trygg sandkasse for å teste endringer.
 */
import { watch } from 'vue'
import { fromIsoDate, nextAllowanceDate, toIsoDate } from './allowance'
import { taskValueOre } from './format'
import { deviceLanguage, dictionaries } from './i18n'

/** Familiekoden barn logger inn med i demoen. Vises på velkomstsiden. */
export const DEMO_FAMILY_CODE = 'DEMO26'

// Demodataene er skrevet på norsk i koden, som resten av appen, og oversettes
// når de sås. Hvert språk får sin egen lagring: den som bytter språk før
// innlogging får en demo på det språket, uten at det man har prøvd i et annet
// språk blir borte.
const STORAGE_KEY = (lang) => `bonto.demo.v7.${lang}`
const SESSION_KEY = 'bonto.demo.session'

// Eldre demodata. v5 lå i én felles nøkkel, fra v6 fikk hvert språk sin egen -
// og v7 er den første med faste lommepenger i datasettet. Gamle sett ryddes
// vekk framfor å bli liggende som noe ingen leser. Kan fjernes når alle
// nettlesere har vært innom én gang.
try {
  localStorage.removeItem('bonto.demo.v5')
  for (const key of Object.keys(localStorage)) {
    if (key.startsWith('bonto.demo.v6.')) localStorage.removeItem(key)
  }
} catch { /* ikke kritisk */ }

const uid = () => 'd' + Math.random().toString(36).slice(2, 11)
const now = () => new Date().toISOString()

/**
 * Oversetter demoteksten til ett bestemt språk - samme oppslag som t() gjør,
 * men uavhengig av hva appen viser akkurat nå. Da kan et datasett sås på nytt
 * i sitt eget språk selv om noen har valgt et annet i familieinnstillingene.
 *
 * Funksjonen kalles `t` der den brukes, slik at `npm run i18n` finner de
 * norske tekstene her på samme måte som ellers i koden.
 */
function translator(lang) {
  return (text) => (dictionaries[lang]?.strings[text] ?? text)
}

function seed(lang) {
  const t = translator(lang)
  const familyId = 'demo-family'
  const parentId = 'demo-parent'
  const kid1 = 'demo-kid-1'
  const kid2 = 'demo-kid-2'
  const catHome = uid()
  const catKitchen = uid()
  const catSchool = uid()
  const readingTask = uid()
  const challengeId = uid()

  const hoursAgo = (h) => new Date(Date.now() - h * 3600_000).toISOString()
  const isoDate = (d) => d.toISOString().slice(0, 10)

  const challengeStart = new Date()
  challengeStart.setDate(challengeStart.getDate() - 14)
  const challengeEnd = new Date(challengeStart)
  challengeEnd.setDate(challengeEnd.getDate() + 60)

  // Tittelen går igjen i oppgaven, i registreringene og i kontoutskriften, og
  // må være den samme teksten alle tre steder.
  const reading = t('Lese 15 minutter')
  const tidyRoom = t('Rydde rommet')
  const vacuum = t('Støvsuge stua')
  const dishwasher = t('Tømme oppvaskmaskinen')

  // Litt leselogg, så demoens utfordring har noe å vise fra første stund.
  const readingLog = { completions: [], transactions: [] }
  const addReading = (childId, daysAgo) => {
    const when = new Date(Date.now() - daysAgo * 86_400_000).toISOString()
    const completionId = uid()
    readingLog.completions.push({
      id: completionId, family_id: familyId, task_id: readingTask, child_id: childId,
      task_title: reading, stars: 1, amount_ore: null, status: 'approved',
      note: null, created_at: when, decided_by: null, decided_at: when, paid: true,
    })
    readingLog.transactions.push({
      id: uid(), family_id: familyId, child_id: childId, type: 'task',
      stars: 1, amount_ore: 1000, description: reading,
      completion_id: completionId, created_by: childId, created_at: when,
    })
  }
  ;[1, 2, 3, 4, 5, 6, 8, 9, 11, 12].forEach((d) => addReading(kid1, d))
  ;[1, 3, 4, 6, 7, 9, 10].forEach((d) => addReading(kid2, d))

  return {
    // Språket dataene er sådd på. Avgjør hvor de lagres, og hvilket språk
    // appen starter demoen på.
    lang,
    family: {
      id: familyId, name: t('Familien'), code: DEMO_FAMILY_CODE,
      star_value_ore: 1000, currency: 'kr', timezone: 'Europe/Oslo', language: lang,
      child_withdrawals_enabled: true,
      child_withdrawal_approval: 'over_limit',
      child_withdrawal_limit_ore: 5000,
    },
    profiles: [
      { id: parentId, family_id: familyId, role: 'parent', display_name: t('Forelder'), emoji: '👤', color: '#4d7c0f', sort_order: 0 },
      // Emma har fast ukelønn, Noah har det ikke - demoen skal vise begge
      // deler, siden det er valgfritt og settes opp per barn.
      {
        id: kid1, family_id: familyId, role: 'child', display_name: 'Emma', emoji: '🦊',
        color: '#f59e0b', sort_order: 1,
        allowance_period: 'weekly', allowance_ore: 5000, allowance_day: 1,
        allowance_next_on: toIsoDate(nextAllowanceDate('weekly', 1)),
      },
      {
        id: kid2, family_id: familyId, role: 'child', display_name: 'Noah', emoji: '🐼',
        color: '#10b981', sort_order: 2,
        allowance_period: 'none', allowance_ore: 0, allowance_day: 1, allowance_next_on: null,
      },
    ],
    pins: { [kid1]: '1234', [kid2]: '1234' },
    categories: [
      { id: catHome, family_id: familyId, name: t('Hjemme'), emoji: '🏠', color: '#4d7c0f', sort_order: 1 },
      { id: catKitchen, family_id: familyId, name: t('Kjøkken'), emoji: '🍽️', color: '#10b981', sort_order: 2 },
      { id: catSchool, family_id: familyId, name: t('Skole'), emoji: '📚', color: '#f59e0b', sort_order: 3 },
    ],
    // Lesing viser fram betalingsgrensen: den kan gjøres tre ganger om dagen
    // - alle tre teller i lesekonkurransen og gir stjerner - men betales kun
    // for den første.
    tasks: [
      { id: uid(), family_id: familyId, category_id: catHome, title: tidyRoom, description: t('Alt på plass, gulvet fritt'), stars: 1, amount_ore: null, requires_approval: true, limit_period: 'day', limit_count: 1, pay_limit_period: 'none', pay_limit_count: 1, assigned_to: null, active: true, sort_order: 1 },
      { id: uid(), family_id: familyId, category_id: catHome, title: vacuum, description: null, stars: 2, amount_ore: null, requires_approval: true, limit_period: 'week', limit_count: 2, pay_limit_period: 'none', pay_limit_count: 1, assigned_to: null, active: true, sort_order: 2 },
      { id: uid(), family_id: familyId, category_id: catKitchen, title: dishwasher, description: null, stars: 1, amount_ore: null, requires_approval: false, limit_period: 'day', limit_count: 2, pay_limit_period: 'none', pay_limit_count: 1, assigned_to: null, active: true, sort_order: 3 },
      { id: uid(), family_id: familyId, category_id: catKitchen, title: t('Dekke bordet'), description: null, stars: 1, amount_ore: null, requires_approval: false, limit_period: 'day', limit_count: 2, pay_limit_period: 'none', pay_limit_count: 1, assigned_to: null, active: true, sort_order: 4 },
      { id: readingTask, family_id: familyId, category_id: catSchool, title: reading, description: t('Bok eller tegneserie teller'), stars: 1, amount_ore: null, requires_approval: false, limit_period: 'day', limit_count: 3, pay_limit_period: 'day', pay_limit_count: 1, assigned_to: null, active: true, sort_order: 5 },
      { id: uid(), family_id: familyId, category_id: catHome, title: t('Vaske bilen'), description: t('Utvendig og innvendig'), stars: 0, amount_ore: 5000, requires_approval: true, limit_period: 'week', limit_count: 1, pay_limit_period: 'none', pay_limit_count: 1, assigned_to: null, active: true, sort_order: 6 },
    ],
    completions: [
      { id: uid(), family_id: familyId, task_id: null, child_id: kid1, task_title: tidyRoom, stars: 1, amount_ore: null, status: 'pending', note: null, created_at: hoursAgo(2), decided_by: null, decided_at: null, paid: false },
      { id: uid(), family_id: familyId, task_id: null, child_id: kid2, task_title: vacuum, stars: 2, amount_ore: null, status: 'pending', note: t('Tok også gangen'), created_at: hoursAgo(5), decided_by: null, decided_at: null, paid: false },
      ...readingLog.completions,
    ],
    transactions: [
      ...readingLog.transactions,
      { id: uid(), family_id: familyId, child_id: kid1, type: 'task', stars: 1, amount_ore: 1000, description: dishwasher, completion_id: null, created_by: kid1, created_at: hoursAgo(30) },
      { id: uid(), family_id: familyId, child_id: kid1, type: 'deposit', stars: 0, amount_ore: 20000, description: t('Bursdagspenger fra bestemor'), completion_id: null, created_by: parentId, created_at: hoursAgo(72) },
      // Ukelønna som kom av seg selv. created_by er null: ingen trykket på noe.
      { id: uid(), family_id: familyId, child_id: kid1, type: 'allowance', stars: 0, amount_ore: 5000, description: t('Ukelønn'), completion_id: null, created_by: null, created_at: hoursAgo(60) },
      { id: uid(), family_id: familyId, child_id: kid1, type: 'allowance', stars: 0, amount_ore: 5000, description: t('Ukelønn'), completion_id: null, created_by: null, created_at: hoursAgo(228) },
      { id: uid(), family_id: familyId, child_id: kid1, type: 'withdrawal', stars: 0, amount_ore: -8900, description: t('Kiosk på fotballcup'), spend_category: 'mat', completion_id: null, created_by: parentId, created_at: hoursAgo(48) },
      { id: uid(), family_id: familyId, child_id: kid1, type: 'withdrawal', stars: 0, amount_ore: -3500, description: t('Smågodt'), spend_category: 'godteri', completion_id: null, created_by: kid1, created_at: hoursAgo(20) },
      { id: uid(), family_id: familyId, child_id: kid2, type: 'task', stars: 2, amount_ore: 2000, description: tidyRoom, completion_id: null, created_by: kid2, created_at: hoursAgo(50) },
      { id: uid(), family_id: familyId, child_id: kid2, type: 'deposit', stars: 0, amount_ore: 10000, description: t('Startsaldo'), completion_id: null, created_by: parentId, created_at: hoursAgo(96) },
    ],
    invites: [],
    challenges: [
      {
        id: challengeId, family_id: familyId,
        title: t('Lesekonkurranse'), description: t('Hvem leser mest?'), emoji: '📚',
        starts_on: isoDate(challengeStart), ends_on: isoDate(challengeEnd),
        goal_type: 'none', goal_target: null,
        prize_ore: 10000, created_by: parentId, created_at: hoursAgo(336),
      },
    ],
    challengeTasks: [{ challenge_id: challengeId, task_id: readingTask }],
    challengeParticipants: [
      { challenge_id: challengeId, child_id: kid1, goal_target: null },
      { challenge_id: challengeId, child_id: kid2, goal_target: null },
    ],
    challengePrizes: [],
    withdrawals: [],
  }
}

function load(lang) {
  try {
    const raw = localStorage.getItem(STORAGE_KEY(lang))
    if (raw) return JSON.parse(raw)
  } catch { /* korrupt lagring - start på nytt */ }
  const fresh = seed(lang)
  localStorage.setItem(STORAGE_KEY(lang), JSON.stringify(fresh))
  return fresh
}

let db = load(deviceLanguage.value)
const save = () => localStorage.setItem(STORAGE_KEY(db.lang), JSON.stringify(db))

// Språkknappen står på sidene før innlogging - der demoen begynner. Velger man
// et annet språk der, skal demofamilien byttes ut med den som snakker det
// språket. Ingen er logget inn på det tidspunktet, så ingen data er i bruk.
watch(deviceLanguage, (lang) => {
  if (lang !== db.lang) db = load(lang)
})

const clone = (value) => JSON.parse(JSON.stringify(value))
const delay = (ms = 90) => new Promise((r) => setTimeout(r, ms))

let authListener = null
function readSession() {
  try {
    const raw = localStorage.getItem(SESSION_KEY)
    return raw ? JSON.parse(raw) : null
  } catch { return null }
}
function writeSession(session) {
  if (session) localStorage.setItem(SESSION_KEY, JSON.stringify(session))
  else localStorage.removeItem(SESSION_KEY)
  authListener?.(session)
}

function currentProfile() {
  const session = readSession()
  if (!session) return null
  return db.profiles.find((p) => p.id === session.user.id) || null
}

function requireParent() {
  const me = currentProfile()
  if (!me || me.role !== 'parent') throw new Error('Kun foreldre kan gjøre dette')
  return me
}

/** Starten på perioden en gitt dato hører til, i lokal tid. Speiler date_trunc i SQL. */
function windowStart(period, at = new Date()) {
  const d = new Date(at)
  d.setHours(0, 0, 0, 0)
  if (period === 'week') {
    const weekday = (d.getDay() + 6) % 7 // mandag = 0
    d.setDate(d.getDate() - weekday)
  }
  return d.getTime()
}

/**
 * Skal registreringen gi penger, eller kun stjerner? Speiler completion_pays
 * i databasen: betalingsgrensen telles i perioden registreringen selv hører
 * til, ikke perioden den blir godkjent i.
 */
function completionPays(completion) {
  const task = db.tasks.find((t) => t.id === completion.task_id)
  if (!task || !task.pay_limit_period || task.pay_limit_period === 'none') return true

  const from = windowStart(task.pay_limit_period, new Date(completion.created_at))
  const to = from + (task.pay_limit_period === 'day' ? 86_400_000 : 7 * 86_400_000)

  const paid = db.completions.filter((c) =>
    c.task_id === task.id &&
    c.child_id === completion.child_id &&
    c.paid &&
    c.id !== completion.id &&
    new Date(c.created_at).getTime() >= from &&
    new Date(c.created_at).getTime() < to).length

  return paid < task.pay_limit_count
}

export const api = {
  isDemo: true,

  // --- Autentisering ------------------------------------------------------

  async getSession() { await delay(20); return readSession() },

  onAuthChange(callback) {
    authListener = callback
    return () => { authListener = null }
  },

  async verifyUser() {
    const session = readSession()
    if (!session) return false
    // Etter en tilbakestilling av demodataene kan sesjonen peke på en profil
    // som ikke finnes lenger.
    return session.user.id === 'demo-parent' || Boolean(currentProfile())
  },

  async signUp({ email }) {
    await delay()
    // I demo finnes bare familien som allerede er satt opp.
    writeSession({ user: { id: 'demo-parent', email } })
    return { needsConfirmation: false, session: readSession() }
  },

  async signIn({ email }) {
    await delay()
    writeSession({ user: { id: 'demo-parent', email: email || 'demo@bonto.no' } })
    return readSession()
  },

  async sendPasswordReset() { await delay() },
  async updatePassword() { await delay() },
  async signOut() { await delay(20); writeSession(null) },

  async listChildrenByFamilyCode(familyCode) {
    await delay()
    if (String(familyCode).trim().toUpperCase() !== db.family.code) {
      throw new Error('Fant ingen familie med den koden')
    }
    return {
      family: { name: db.family.name, code: db.family.code },
      children: db.profiles
        .filter((p) => p.role === 'child')
        .map(({ id, display_name, emoji, color }) => ({ id, display_name, emoji, color })),
    }
  },

  async childLogin({ childId, pin }) {
    await delay()
    if (db.pins[childId] !== String(pin)) throw new Error('Feil PIN-kode')
    writeSession({ user: { id: childId } })
    return readSession()
  },

  // --- Oppsett ------------------------------------------------------------

  async createFamily({ familyName, displayName }) {
    await delay()
    db.family.name = familyName
    const me = currentProfile()
    if (me) me.display_name = displayName
    save()
    return { family_id: db.family.id, code: db.family.code }
  },

  async joinFamily() { await delay(); return { family_id: db.family.id } },

  async createInvite() {
    await delay()
    const code = 'DEMO' + Math.random().toString(36).slice(2, 8).toUpperCase()
    db.invites.push({ code, family_id: db.family.id, created_at: now() })
    save()
    return code
  },

  // --- Grunndata ----------------------------------------------------------

  async loadProfile() { await delay(20); return clone(currentProfile()) },
  async loadFamily() { await delay(20); return clone(db.family) },

  async loadMembers() {
    await delay(20)
    return clone(db.profiles).sort((a, b) =>
      a.role.localeCompare(b.role) || a.sort_order - b.sort_order)
  },

  async loadBalances() {
    await delay(20)
    return db.profiles.filter((p) => p.role === 'child').map((child) => {
      const rows = db.transactions.filter((t) => t.child_id === child.id)
      return {
        child_id: child.id,
        family_id: db.family.id,
        balance_ore: rows.reduce((sum, t) => sum + t.amount_ore, 0),
        total_stars: rows.reduce((sum, t) => sum + (t.stars || 0), 0),
      }
    })
  },

  async updateFamily(patch) { await delay(); requireParent(); Object.assign(db.family, patch); save() },

  async updateProfile(id, patch) {
    await delay()
    const profile = db.profiles.find((p) => p.id === id)
    if (profile) Object.assign(profile, patch)
    save()
  },

  // --- Barn ---------------------------------------------------------------

  async createChild({ displayName, emoji, color, pin }) {
    await delay()
    requireParent()
    const child = {
      id: uid(), family_id: db.family.id, role: 'child',
      display_name: displayName, emoji: emoji || '🙂', color: color || '#4d7c0f',
      sort_order: db.profiles.length,
      allowance_period: 'none', allowance_ore: 0, allowance_day: 1, allowance_next_on: null,
    }
    db.profiles.push(child)
    db.pins[child.id] = String(pin)
    save()
    return { profile: clone(child) }
  },

  async setChildPin(childId, pin) { await delay(); requireParent(); db.pins[childId] = String(pin); save(); return {} },

  // --- Fast lommepenger ---------------------------------------------------

  async setChildAllowance({ childId, period, amountOre, day }) {
    await delay()
    requireParent()
    const child = db.profiles.find((p) => p.id === childId && p.role === 'child')
    if (!child) throw new Error('Fant ikke barnet')
    if (period !== 'none' && !(amountOre > 0)) {
      throw new Error('Faste lommepenger må være mer enn null')
    }

    child.allowance_period = period
    // Beløpet blir stående når lommepengene skrus av, så de kan skrus på igjen
    // uten å taste det inn på nytt - samme regel som i set_child_allowance().
    if (period !== 'none') child.allowance_ore = amountOre
    child.allowance_day = day ?? 1
    child.allowance_next_on = period === 'none'
      ? null
      : toIsoDate(nextAllowanceDate(period, child.allowance_day))
    save()
    return { next_on: child.allowance_next_on }
  },

  /**
   * Demoen har ingen statistikk å føre - dataene finnes bare i denne
   * nettleseren. Finnes likevel, slik at de to datalagene er like utenpå.
   */
  async touchLastSeen() {},

  /**
   * Fører de faste lommepengene som har forfalt. Speiler
   * run_due_allowances() i databasen: alle utbetalinger som skulle ha kommet
   * blir ført, også om det har gått lang tid siden noen åpnet demoen.
   */
  async runDueAllowances() {
    const t = translator(db.lang)
    const today = new Date()
    today.setHours(0, 0, 0, 0)
    let paid = 0

    for (const child of db.profiles) {
      if (child.role !== 'child') continue
      if (!child.allowance_period || child.allowance_period === 'none') continue
      if (!(child.allowance_ore > 0) || !child.allowance_next_on) continue

      let next = fromIsoDate(child.allowance_next_on)
      let guard = 0
      while (next <= today && guard < 60) {
        db.transactions.push({
          id: uid(), family_id: db.family.id, child_id: child.id, type: 'allowance',
          stars: 0, amount_ore: child.allowance_ore,
          description: child.allowance_period === 'weekly' ? t('Ukelønn') : t('Månedslønn'),
          completion_id: null, created_by: null, created_at: next.toISOString(),
        })
        next = nextAllowanceDate(child.allowance_period, child.allowance_day, next)
        paid++
        guard++
      }
      child.allowance_next_on = toIsoDate(next)
    }

    if (paid) save()
    return paid
  },

  async deleteChild(childId) {
    await delay()
    requireParent()
    db.profiles = db.profiles.filter((p) => p.id !== childId)
    db.transactions = db.transactions.filter((t) => t.child_id !== childId)
    db.completions = db.completions.filter((c) => c.child_id !== childId)
    delete db.pins[childId]
    save()
    return {}
  },

  // --- Kategorier ---------------------------------------------------------

  async listCategories() { await delay(20); return clone(db.categories).sort((a, b) => a.sort_order - b.sort_order) },

  async createCategory(payload) {
    await delay()
    requireParent()
    const category = { id: uid(), family_id: db.family.id, sort_order: db.categories.length + 1, ...payload }
    db.categories.push(category)
    save()
    return clone(category)
  },

  async updateCategory(id, patch) {
    await delay()
    requireParent()
    Object.assign(db.categories.find((c) => c.id === id) || {}, patch)
    save()
  },

  async deleteCategory(id) {
    await delay()
    requireParent()
    db.categories = db.categories.filter((c) => c.id !== id)
    db.tasks.forEach((t) => { if (t.category_id === id) t.category_id = null })
    save()
  },

  // --- Oppgaver -----------------------------------------------------------

  async listTasks() { await delay(20); return clone(db.tasks).sort((a, b) => a.sort_order - b.sort_order) },

  async createTask(payload) {
    await delay()
    requireParent()
    const task = {
      id: uid(), family_id: db.family.id, active: true, assigned_to: null,
      sort_order: db.tasks.length + 1, description: null, ...payload,
    }
    db.tasks.push(task)
    save()
    return clone(task)
  },

  async updateTask(id, patch) {
    await delay()
    requireParent()
    Object.assign(db.tasks.find((t) => t.id === id) || {}, patch)
    save()
  },

  async deleteTask(id) {
    await delay()
    requireParent()
    db.tasks = db.tasks.filter((t) => t.id !== id)
    save()
  },

  // --- Registreringer -----------------------------------------------------

  async listCompletions({ status, childId, limit = 100 } = {}) {
    await delay(20)
    return clone(db.completions)
      .filter((c) => (!status || c.status === status) && (!childId || c.child_id === childId))
      .sort((a, b) => b.created_at.localeCompare(a.created_at))
      .slice(0, limit)
  },

  async submitCompletion(taskId, note) {
    await delay()
    const me = currentProfile()
    if (!me || me.role !== 'child') throw new Error('Kun barn kan registrere oppgaver')

    const task = db.tasks.find((t) => t.id === taskId)
    if (!task) throw new Error('Fant ikke oppgaven')
    if (!task.active) throw new Error('Oppgaven er ikke aktiv')
    if (task.assigned_to && task.assigned_to !== me.id) throw new Error('Denne oppgaven er tildelt noen andre')

    if (task.limit_period !== 'none') {
      const since = windowStart(task.limit_period)
      const used = db.completions.filter((c) =>
        c.task_id === task.id && c.child_id === me.id &&
        c.status !== 'rejected' && new Date(c.created_at).getTime() >= since).length
      if (used >= task.limit_count) {
        const when = task.limit_period === 'day' ? 'i dag' : 'denne uka'
        throw new Error(`Du har allerede gjort denne oppgaven ${task.limit_count} gang(er) ${when}`)
      }
    }

    const completion = {
      id: uid(), family_id: db.family.id, task_id: task.id, child_id: me.id,
      task_title: task.title, stars: task.stars, amount_ore: task.amount_ore,
      status: task.requires_approval ? 'pending' : 'approved',
      note: note?.trim() || null, created_at: now(),
      decided_by: null, decided_at: task.requires_approval ? null : now(),
      paid: false,
    }
    db.completions.push(completion)

    if (!task.requires_approval) {
      // Utover betalingsgrensen føres registreringen med stjernene sine, men
      // uten penger.
      completion.paid = completionPays(completion)
      db.transactions.push({
        id: uid(), family_id: db.family.id, child_id: me.id, type: 'task',
        stars: completion.stars,
        amount_ore: completion.paid ? taskValueOre(completion, db.family.star_value_ore) : 0,
        description: task.title, completion_id: completion.id,
        created_by: me.id, created_at: now(),
      })
    }
    save()
    return { id: completion.id, status: completion.status, paid: completion.paid }
  },

  async submitCompletionFor(childId, taskId, note) {
    await delay()
    const me = requireParent()

    const child = db.profiles.find((p) => p.id === childId && p.role === 'child')
    if (!child) throw new Error('Fant ikke barnet')

    const task = db.tasks.find((t) => t.id === taskId)
    if (!task) throw new Error('Fant ikke oppgaven')
    if (!task.active) throw new Error('Oppgaven er ikke aktiv')
    if (task.assigned_to && task.assigned_to !== child.id) throw new Error('Denne oppgaven er tildelt noen andre')

    if (task.limit_period !== 'none') {
      const since = windowStart(task.limit_period)
      const used = db.completions.filter((c) =>
        c.task_id === task.id && c.child_id === child.id &&
        c.status !== 'rejected' && new Date(c.created_at).getTime() >= since).length
      if (used >= task.limit_count) {
        const when = task.limit_period === 'day' ? 'i dag' : 'denne uka'
        throw new Error(
          `${child.display_name} har allerede gjort denne oppgaven ${task.limit_count} gang(er) ${when}`)
      }
    }

    // Forelderen registrerer og godkjenner i samme handling.
    const completion = {
      id: uid(), family_id: db.family.id, task_id: task.id, child_id: child.id,
      task_title: task.title, stars: task.stars, amount_ore: task.amount_ore,
      status: 'approved', note: note?.trim() || null, created_at: now(),
      decided_by: me.id, decided_at: now(), paid: false,
    }
    db.completions.push(completion)

    completion.paid = completionPays(completion)
    db.transactions.push({
      id: uid(), family_id: db.family.id, child_id: child.id, type: 'task',
      stars: completion.stars,
      amount_ore: completion.paid ? taskValueOre(completion, db.family.star_value_ore) : 0,
      description: task.title, completion_id: completion.id,
      created_by: me.id, created_at: now(),
    })
    save()
    return { id: completion.id, status: 'approved', paid: completion.paid }
  },

  async decideCompletion(id, approve) {
    await delay()
    requireParent()
    const me = currentProfile()
    const completion = db.completions.find((c) => c.id === id)
    if (!completion) throw new Error('Fant ikke registreringen')
    if (completion.status !== 'pending') throw new Error('Denne er allerede behandlet')

    completion.status = approve ? 'approved' : 'rejected'
    completion.decided_by = me.id
    completion.decided_at = now()

    if (approve) {
      completion.paid = completionPays(completion)
      db.transactions.push({
        id: uid(), family_id: db.family.id, child_id: completion.child_id, type: 'task',
        stars: completion.stars,
        amount_ore: completion.paid ? taskValueOre(completion, db.family.star_value_ore) : 0,
        description: completion.task_title, completion_id: completion.id,
        created_by: me.id, created_at: now(),
      })
    }
    save()
    return { id, approved: approve, paid: completion.paid }
  },

  async undoCompletion(id) {
    await delay()
    requireParent()
    db.transactions = db.transactions.filter((t) => t.completion_id !== id)
    db.completions = db.completions.filter((c) => c.id !== id)
    save()
  },

  // --- Utfordringer -------------------------------------------------------

  async loadChallengeData() {
    await delay(20)
    // Speiler visningen challenge_standings i databasen.
    const standings = []
    for (const challenge of db.challenges) {
      const taskIds = db.challengeTasks
        .filter((ct) => ct.challenge_id === challenge.id)
        .map((ct) => ct.task_id)

      for (const participant of db.challengeParticipants.filter((p) => p.challenge_id === challenge.id)) {
        const done = db.completions.filter((c) =>
          c.child_id === participant.child_id &&
          taskIds.includes(c.task_id) &&
          c.status === 'approved' &&
          c.created_at.slice(0, 10) >= challenge.starts_on &&
          c.created_at.slice(0, 10) <= challenge.ends_on)

        const earned = done.reduce((sum, completion) => {
          const txn = db.transactions.find((t) => t.completion_id === completion.id)
          return sum + (txn?.amount_ore ?? 0)
        }, 0)

        standings.push({
          challenge_id: challenge.id,
          family_id: db.family.id,
          child_id: participant.child_id,
          goal_target: participant.goal_target,
          completions: done.length,
          stars: done.reduce((sum, c) => sum + c.stars, 0),
          earned_ore: earned,
        })
      }
    }

    return {
      challenges: clone(db.challenges),
      tasks: clone(db.challengeTasks),
      participants: clone(db.challengeParticipants),
      standings,
      prizes: clone(db.challengePrizes),
    }
  },

  async saveChallenge(payload) {
    await delay()
    requireParent()
    if (!payload.title?.trim()) throw new Error('Utfordringen trenger et navn')
    if (payload.ends_on < payload.starts_on) throw new Error('Sluttdatoen må være etter startdatoen')
    if (!payload.task_ids?.length) throw new Error('Velg minst én oppgave som skal telle')
    if (payload.goal_type === 'shared' && !(payload.goal_target > 0)) {
      throw new Error('Et felles mål må ha et måltall')
    }
    if (!payload.participants?.length) throw new Error('Velg minst ett barn som skal delta')

    const id = payload.id ?? uid()
    const row = {
      id,
      family_id: db.family.id,
      title: payload.title.trim(),
      description: payload.description?.trim() || null,
      emoji: payload.emoji || '🏆',
      starts_on: payload.starts_on,
      ends_on: payload.ends_on,
      goal_type: payload.goal_type,
      goal_target: payload.goal_type === 'shared' ? payload.goal_target : null,
      prize_ore: Math.max(payload.prize_ore || 0, 0),
      created_by: currentProfile().id,
      created_at: now(),
    }

    const existing = db.challenges.findIndex((c) => c.id === id)
    if (existing >= 0) db.challenges[existing] = { ...db.challenges[existing], ...row }
    else db.challenges.push(row)

    db.challengeTasks = db.challengeTasks.filter((ct) => ct.challenge_id !== id)
    payload.task_ids.forEach((taskId) => db.challengeTasks.push({ challenge_id: id, task_id: taskId }))

    db.challengeParticipants = db.challengeParticipants.filter((p) => p.challenge_id !== id)
    payload.participants.forEach((p) => db.challengeParticipants.push({
      challenge_id: id,
      child_id: p.child_id,
      goal_target: payload.goal_type === 'per_child' ? (p.goal_target || null) : null,
    }))

    save()
    return id
  },

  async deleteChallenge(id) {
    await delay()
    requireParent()
    db.challenges = db.challenges.filter((c) => c.id !== id)
    db.challengeTasks = db.challengeTasks.filter((ct) => ct.challenge_id !== id)
    db.challengeParticipants = db.challengeParticipants.filter((p) => p.challenge_id !== id)
    db.challengePrizes = db.challengePrizes.filter((p) => p.challenge_id !== id)
    save()
  },

  async payChallengePrize(challengeId, childId) {
    await delay()
    const me = requireParent()
    const challenge = db.challenges.find((c) => c.id === challengeId)
    if (!challenge) throw new Error('Fant ikke utfordringen')
    if (challenge.prize_ore <= 0) throw new Error('Utfordringen har ingen premie')
    if (db.challengePrizes.some((p) => p.challenge_id === challengeId && p.child_id === childId)) {
      throw new Error('Premien er allerede utbetalt til dette barnet')
    }

    const transaction = {
      id: uid(), family_id: db.family.id, child_id: childId, type: 'prize',
      stars: 0, amount_ore: challenge.prize_ore,
      description: `Premie: ${challenge.title}`,
      completion_id: null, created_by: me.id, created_at: now(),
    }
    db.transactions.push(transaction)
    db.challengePrizes.push({
      challenge_id: challengeId, child_id: childId,
      amount_ore: challenge.prize_ore, transaction_id: transaction.id,
      paid_by: me.id, paid_at: now(),
    })
    save()
    return { transaction_id: transaction.id, amount_ore: challenge.prize_ore }
  },

  async undoChallengePrize(challengeId, childId) {
    await delay()
    requireParent()
    const prize = db.challengePrizes.find((p) => p.challenge_id === challengeId && p.child_id === childId)
    if (!prize) throw new Error('Fant ingen utbetaling å angre')
    db.transactions = db.transactions.filter((t) => t.id !== prize.transaction_id)
    db.challengePrizes = db.challengePrizes.filter((p) => p !== prize)
    save()
  },

  // --- Penger -------------------------------------------------------------

  async listTransactions({ childId, limit = 100 } = {}) {
    await delay(20)
    return clone(db.transactions)
      .filter((t) => !childId || t.child_id === childId)
      .sort((a, b) => b.created_at.localeCompare(a.created_at))
      .slice(0, limit)
  },

  async addTransaction({ childId, type, amountOre, description, spendCategory }) {
    await delay()
    const me = requireParent()
    if (!amountOre) throw new Error('Beløpet kan ikke være null')

    const signed = type === 'withdrawal' ? -Math.abs(amountOre)
      : type === 'deposit' ? Math.abs(amountOre)
        : amountOre

    // Tom tekst lagres tom - grensesnittet setter inn ordet på familiens
    // språk (transactionLabel i lib/spending.js), slik basen også gjør det.
    const transaction = {
      id: uid(), family_id: db.family.id, child_id: childId, type,
      stars: 0, amount_ore: signed, description: description?.trim() || '',
      spend_category: type === 'withdrawal' ? (spendCategory || null) : null,
      completion_id: null, created_by: me.id, created_at: now(),
    }
    db.transactions.push(transaction)
    save()
    return { id: transaction.id, amount_ore: signed }
  },

  // --- Barnets egne uttak -------------------------------------------------

  async listWithdrawals({ status, childId, limit = 200 } = {}) {
    await delay(20)
    return clone(db.withdrawals)
      .filter((w) => (!status || w.status === status) && (!childId || w.child_id === childId))
      .sort((a, b) => b.created_at.localeCompare(a.created_at))
      .slice(0, limit)
  },

  async submitWithdrawal({ amountOre, description, spendCategory }) {
    await delay()
    const me = currentProfile()
    if (!me || me.role !== 'child') throw new Error('Kun barn bruker denne')
    if (!db.family.child_withdrawals_enabled) {
      throw new Error('Foreldrene dine har slått av at du kan føre opp kjøp selv')
    }
    if (!(amountOre > 0)) throw new Error('Skriv inn et beløp')

    const balance = db.transactions
      .filter((t) => t.child_id === me.id)
      .reduce((sum, t) => sum + t.amount_ore, 0)
    const pending = db.withdrawals
      .filter((w) => w.child_id === me.id && w.status === 'pending')
      .reduce((sum, w) => sum + w.amount_ore, 0)
    const available = balance - pending

    if (amountOre > available) {
      throw new Error(`Du har bare ${(available / 100).toFixed(2).replace('.', ',')} kr igjen`)
    }

    const needsApproval = db.family.child_withdrawal_approval === 'always'
      ? true
      : db.family.child_withdrawal_approval === 'never'
        ? false
        : amountOre > db.family.child_withdrawal_limit_ore

    const row = {
      id: uid(), family_id: db.family.id, child_id: me.id,
      amount_ore: amountOre,
      description: description?.trim() || null,
      spend_category: spendCategory || null,
      status: needsApproval ? 'pending' : 'approved',
      transaction_id: null, created_at: now(),
      decided_by: null, decided_at: needsApproval ? null : now(),
    }
    db.withdrawals.push(row)

    if (!needsApproval) {
      const transaction = {
        id: uid(), family_id: db.family.id, child_id: me.id, type: 'withdrawal',
        stars: 0, amount_ore: -amountOre,
        description: row.description || '', spend_category: row.spend_category,
        completion_id: null, created_by: me.id, created_at: now(),
      }
      db.transactions.push(transaction)
      row.transaction_id = transaction.id
    }

    save()
    return { id: row.id, status: row.status }
  },

  async decideWithdrawal(id, approve) {
    await delay()
    const me = requireParent()
    const row = db.withdrawals.find((w) => w.id === id)
    if (!row) throw new Error('Fant ikke uttaket')
    if (row.status !== 'pending') throw new Error('Dette er allerede behandlet')

    row.status = approve ? 'approved' : 'rejected'
    row.decided_by = me.id
    row.decided_at = now()

    if (approve) {
      const transaction = {
        id: uid(), family_id: db.family.id, child_id: row.child_id, type: 'withdrawal',
        stars: 0, amount_ore: -row.amount_ore,
        description: row.description || '', spend_category: row.spend_category,
        completion_id: null, created_by: me.id, created_at: now(),
      }
      db.transactions.push(transaction)
      row.transaction_id = transaction.id
    }

    save()
    return { id, approved: approve }
  },

  async undoWithdrawal(id) {
    await delay()
    requireParent()
    const row = db.withdrawals.find((w) => w.id === id)
    if (!row) throw new Error('Fant ikke uttaket')
    db.transactions = db.transactions.filter((t) => t.id !== row.transaction_id)
    db.withdrawals = db.withdrawals.filter((w) => w.id !== id)
    save()
  },

  async deleteTransaction(id) {
    await delay()
    requireParent()
    db.transactions = db.transactions.filter((t) => t.id !== id || t.completion_id)
    save()
  },

  async updateTransaction(id, amountOre, description) {
    await delay()
    requireParent()
    const transaction = db.transactions.find((t) => t.id === id)
    if (!transaction) throw new Error('Fant ikke posteringen')
    if (amountOre === null || amountOre === undefined) throw new Error('Skriv inn et beløp')
    if (!amountOre && ['deposit', 'withdrawal', 'adjustment'].includes(transaction.type)) {
      throw new Error('Beløpet kan ikke være null')
    }

    const signed = transaction.type === 'withdrawal' ? -Math.abs(amountOre)
      : transaction.type === 'adjustment' ? amountOre
        : Math.abs(amountOre)
    transaction.amount_ore = signed
    // null betyr «la teksten stå», tom streng betyr «fjern den».
    if (description !== null && description !== undefined) {
      transaction.description = description.trim().slice(0, 200)
    }

    // Kilden holdes i takt med hovedboka, samme som i databasen.
    const withdrawal = db.withdrawals.find((w) => w.transaction_id === id)
    if (withdrawal) {
      withdrawal.amount_ore = Math.abs(signed)
      withdrawal.description = transaction.description || null
    }
    const prize = db.challengePrizes.find((p) => p.transaction_id === id)
    if (prize) prize.amount_ore = signed

    save()
    return { id, amount_ore: signed, description: transaction.description }
  },

  // Oversettelsen ligger i ordbøkene som bygges av `npm run i18n` og bakes inn
  // i appen, ikke i datalaget - demoen og et ekte oppsett har derfor nøyaktig
  // de samme språkene å velge mellom.

  // --- Kun for demo -------------------------------------------------------

  resetDemo() {
    db = seed(db.lang)
    save()
    writeSession(null)
  },

  demoChildPin(childId) { return db.pins[childId] },
}
